<template>
  <main-template>
    <!-- Header -->
    <v-app-bar color="#1e40af" height="120" flat>
      <v-container class="text-center white--text">
        <h1 class="text-h5 font-weight-bold">📚 Calculadora FUNDEB {{ anoAtual }}</h1>
        <p class="text-subtitle-2 mt-1" style="color: #bfdbfe">
          Sistema de Cálculo de Incremento do Orçamento Educacional
        </p>
      </v-container>
    </v-app-bar>

    <v-container class="py-8" max-width="1200">
      <v-row>
        <v-col cols="12" md="8">
          <!-- Formulário -->
          <v-card class="pa-6 mb-8 rounded-lg shadow-sm" :loading="loadingParams">
            <v-card-title class="text-h6 font-weight-bold align-center d-flex">
              <v-avatar color="#1e40af" size="32" class="mr-3">
                <span class="white--text">1</span>
              </v-avatar>
              Dados do Município (Ano Ref: {{ anoAtual }})
            </v-card-title>

            <v-divider class="mb-4"></v-divider>

            <v-card-text v-if="parametros">
              <ValidationObserver v-slot="{ handleSubmit }">
                <form @submit.prevent="handleSubmit(calcularOrcamento)" :disabled="submittingForm">
                  <v-row dense>
                    <v-col class="pt-0 pb-0" cols="6" md="6">
                      <ValidationProvider
                        name="Nome do Município"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <e-label>Nome do Município *</e-label>
                        <v-text-field
                          v-model="municipio.nome"
                          variant="outlined"
                          density="comfortable"
                          required
                          dense
                          solo
                          :error-messages="errors"
                          @input="municipio.nome = municipio.nome.replace(/[^a-zA-ZÀ-ÿ\s]/g, '')"
                        />
                      </ValidationProvider>
                    </v-col>

                    <v-col class="pt-0 pb-0" cols="6" md="6">
                      <e-label>UF *</e-label>
                      <ValidationProvider name="UF" rules="required" v-slot="{ errors }">
                        <e-autocomplete
                          v-model="municipio.uf"
                          :items="$constants.estadosList"
                          :item-text="(item) => item.codigo"
                          :item-value="(item) => item.codigo"
                          variant="outlined"
                          density="comfortable"
                          required
                          dense
                          solo
                          :error-messages="errors"
                        />
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="pt-0 pb-0" cols="12" md="6">
                      <ValidationProvider
                        name="Receitas da Educação (R$)"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <e-label>Receitas da Educação (R$) *</e-label>
                        <v-text-field
                          v-model.number="municipio.receitas_educacao"
                          type="number"
                          variant="outlined"
                          density="comfortable"
                          required
                          dense
                          solo
                          :error-messages="errors"
                          min="0"
                          @input="
                            municipio.receitas_educacao = Math.max(
                              0,
                              municipio.receitas_educacao || 0
                            )
                          "
                        />
                      </ValidationProvider>
                    </v-col>
                  </v-row>

                  <!-- Matrículas -->
                  <h3
                    class="d-flex align-center text-subtitle-1 font-weight-medium text-grey-darken-3 mb-4 mt-6"
                  >
                    <v-avatar color="green" size="28" class="mr-2">
                      <span class="text-body-2">📊</span>
                    </v-avatar>
                    Matrículas por Segmento de Ensino
                  </h3>

                  <v-row dense>
                    <v-col
                      v-for="(segmento, key) in fatoresPonderacao"
                      :key="key"
                      cols="3"
                      md="4"
                      class="pt-0 pb-0"
                    >
                      <e-label>{{ segmento.nome }} </e-label> <br />
                      <e-label class="text-caption">
                        VAAF: {{ segmento.fp_vaaf }} | VAAT: {{ segmento.fp_vaat }}
                      </e-label>
                      <v-text-field
                        v-model.number="municipio.matriculas_por_segmento[key]"
                        persistent-hint
                        type="number"
                        min="0"
                        variant="outlined"
                        density="comfortable"
                        dense
                        solo
                      />
                    </v-col>
                  </v-row>

                  <!-- Condicionalidades -->
                  <h3
                    class="d-flex align-center text-subtitle-1 font-weight-medium text-grey-darken-3 mb-4 mt-6"
                  >
                    <v-avatar color="yellow darken-4" size="28" class="mr-2 white--text">
                      <span class="text-body-2">✓</span>
                    </v-avatar>
                    Condicionalidades VAAR
                  </h3>

                  <v-checkbox
                    v-for="(cond, index) in condicionalidades"
                    :key="index"
                    v-model="municipio.condicionalidades[index]"
                    hide-details
                    density="comfortable"
                    color="#1e40af"
                  >
                    <template #label>
                      <span v-html="cond"></span>
                    </template>
                  </v-checkbox>

                  <div class="text-center mt-6">
                    <v-btn
                      class="white--text px-8"
                      color="#1e40af"
                      :loading="submittingForm"
                      :disabled="submittingForm"
                      type="submit"
                      size="large"
                    >
                      🧮 Calcular e Salvar Orçamento
                    </v-btn>
                  </div>
                </form>
              </ValidationObserver>
            </v-card-text>
            <v-alert v-else type="error" class="ma-4">
              Não foi possível carregar os parâmetros do FUNDEB para o ano {{ anoAtual }}.
            </v-alert>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <!-- Histórico -->
          <v-card class="pa-4 rounded-lg shadow-sm" style="max-height: 800px; overflow-y: auto">
            <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
              <v-icon class="mr-2" color="#1e40af">mdi-history</v-icon>
              Histórico de Cálculos
            </v-card-title>
            <v-divider class="mb-2"></v-divider>
            <v-list v-if="historico.length > 0">
              <v-list-item
                v-for="calc in historico"
                :key="calc.id"
                @click="carregarCalculoHistorico(calc)"
                class="mb-2 rounded border clickable-item"
              >
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">
                    {{ calc.municipio }} - {{ calc.uf }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Ano: {{ calc.ano ? calc.ano.descricao : "N/A" }} |
                    {{ formatDate(calc.created_at) }}
                  </v-list-item-subtitle>
                  <div class="text-caption font-weight-medium text-success">
                    Incremento: {{ formatarMoeda(calc.resultado.incrementoTotal) }}
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <div v-else class="text-center pa-4 text-grey">Nenhum histórico encontrado.</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Resultados -->
      <v-slide-y-transition>
        <div v-if="resultado" id="secao-resultado">
          <!-- Resumo Executivo -->
          <v-card class="pa-6 mb-6 rounded-lg shadow-sm">
            <v-card-title class="text-h6 font-weight-bold align-center d-flex mb-4">
              <v-avatar color="green" size="32" class="mr-3">
                <span class="white--text">2</span>
              </v-avatar>
              Resumo Executivo (Ref: {{ resultado.ano }})
            </v-card-title>

            <v-row dense>
              <v-col cols="12" md="3" v-for="item in resumo" :key="item.titulo">
                <v-sheet
                  class="pa-4 rounded-lg text-left"
                  elevation="1"
                  :color="item.color || 'blue-lighten-5'"
                  style="border-left: 4px solid"
                  :style="`border-left-color: ${item.borderColor || '#2196F3'}`"
                >
                  <div class="text-caption text-grey-darken-1 mb-1">
                    {{ item.titulo }}
                  </div>
                  <div
                    class="text-h6 font-weight-bold"
                    :style="`color: ${item.valueColor || '#000'}`"
                  >
                    {{ item.valor }}
                  </div>
                </v-sheet>
              </v-col>
            </v-row>
          </v-card>

          <!-- Detalhamento dos Complementos -->
          <v-card class="pa-6 mb-6 rounded-lg shadow-sm">
            <v-card-title class="text-h6 font-weight-bold mb-4">
              💰 Detalhamento dos Complementos (Índices {{ resultado.ano }})
            </v-card-title>

            <v-card-text class="pa-0">
              <div v-for="(comp, key) in resultado.complementos" :key="key" class="mb-3">
                <v-sheet
                  class="pa-4 rounded-lg d-flex justify-space-between align-center"
                  elevation="1"
                  :color="comp.recebe ? 'light-green lighten-5' : 'grey-lighten-4'"
                >
                  <div>
                    <div class="font-weight-bold text-body-1">
                      {{ comp.label }}
                    </div>
                    <div class="text-caption text-grey-darken-1">
                      {{ comp.mensagem }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-h6 font-weight-bold">
                      {{ formatarMoeda(comp.valor) }}
                    </div>
                    <v-chip :color="comp.recebe ? 'green' : 'grey'" size="small" variant="flat">
                      {{ comp.recebe ? "Elegível" : "Não Elegível" }}
                    </v-chip>
                  </div>
                </v-sheet>
              </div>
            </v-card-text>
          </v-card>

          <!-- Estratégias para Ampliar Orçamento -->
          <v-card class="pa-6 mb-6 rounded-lg shadow-sm">
            <v-card-title class="text-h6 font-weight-bold mb-4">
              🎯 Estratégias para Ampliar Orçamento
            </v-card-title>

            <v-card-text class="pa-0">
              <!-- Maximizar Fatores de Ponderação -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="light-blue lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <span class="font-weight-bold text-body-1 text-blue"
                    >🎯 Maximizar Fatores de Ponderação</span
                  >
                </div>
                <v-list density="compact" class="bg-transparent" color="light-blue lighten-5">
                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Priorizar creche integral pública (fator 1,9 VAAT)</v-list-item-title
                    >
                  </v-list-item>

                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Expandir pré-escola integral pública (fator 1,88 VAAT)</v-list-item-title
                    >
                  </v-list-item>

                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Implementar ensino fundamental integral (fator 1,5)</v-list-item-title
                    >
                  </v-list-item>

                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon size="small" color="blue">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2"
                      >Desenvolver ensino médio integral (fator 1,52)</v-list-item-title
                    >
                  </v-list-item>
                </v-list>
              </v-sheet>

              <!-- Modalidades Especiais -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="light-green lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <v-icon color="green" class="mr-2">mdi-school</v-icon>
                  <span class="font-weight-bold text-body-1"
                    >Modalidades Especiais (Fatores Elevados)</span
                  >
                </div>
                <v-list density="compact" class="bg-transparent" color="light-green lighten-5">
                  <v-list-item
                    v-for="modalidade in modalidadesEspeciais"
                    :key="modalidade"
                    class="px-0"
                  >
                    <template v-slot:prepend>
                      <v-icon size="small" color="green">mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ modalidade }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-sheet>

              <!-- Status das Condicionalidades VAAR -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="yellow lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <span class="font-weight-bold text-body-1"
                    >✅ Status das Condicionalidades VAAR</span
                  >
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[0] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[0] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Gestão Escolar</span>
                    </div>
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[1] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[1] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Participação dos Estudantes</span>
                    </div>
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[2] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[2] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Redução de Desigualdades</span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[3] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[3] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Regime de Colaboração</span>
                    </div>
                    <div class="mb-2">
                      <v-icon
                        :color="municipio.condicionalidades[4] ? 'green' : 'red'"
                        size="small"
                        class="mr-2"
                      >
                        {{ municipio.condicionalidades[4] ? "✅" : "❌" }}
                      </v-icon>
                      <span class="text-body-2">Currículo BNCC</span>
                    </div>
                  </v-col>
                </v-row>
              </v-sheet>

              <!-- Oportunidades de Complementos -->
              <v-sheet class="pa-4 mb-4 rounded-lg" color="indigo lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <v-icon color="purple" class="mr-2">mdi-star</v-icon>
                  <span class="font-weight-bold text-body-1">Oportunidades de Complementos</span>
                </div>
                <div v-for="oportunidade in oportunidades" :key="oportunidade.tipo" class="mb-2">
                  <v-sheet
                    class="pa-3 rounded d-flex justify-space-between align-center"
                    :color="oportunidade.tipo.includes('❌') ? 'red lighten-5' : 'green lighten-5'"
                    elevation="0"
                  >
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{ oportunidade.tipo }}</span>
                    </div>
                    <span class="font-weight-bold">{{ oportunidade.valor }}</span>
                  </v-sheet>
                  <div
                    v-if="!oportunidade.elegivel && oportunidade.observacao"
                    class="text-caption text-red mt-1 ml-6"
                  >
                    {{ oportunidade.observacao }}
                  </div>
                </div>
              </v-sheet>

              <!-- Análise das Matrículas Atuais -->
              <v-sheet class="pa-4 rounded-lg" color="indigo lighten-5" elevation="1">
                <div class="d-flex align-center mb-3">
                  <v-icon color="blue-grey" class="mr-2">mdi-chart-bar</v-icon>
                  <span class="font-weight-bold text-indigo darken-2"
                    >Análise das Matrículas Atuais</span
                  >
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="text-body-2 font-weight-medium mb-2">Principais Segmentos</div>
                    <div
                      v-for="(quantidade, segmento) in analiseMatriculas.segmentos"
                      :key="segmento"
                      class="mb-1"
                    >
                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-xs font-medium">
                          {{ fatoresPonderacao[segmento]?.nome || segmento }}
                        </span>
                        <span class="text-xs font-medium">
                          {{ quantidade }} ({{ calcularPercentual(quantidade) }}%)
                        </span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="text-body-2 font-weight-medium mb-2">Resumo Geral</div>
                    <div class="text-body-2 mb-1">
                      <span class="font-weight-medium">Total de Matrículas:</span>
                      <span class="float-right">{{ analiseMatriculas.total }}</span>
                    </div>
                    <div class="text-body-2 mb-1">
                      <span class="font-weight-medium">Matrículas Pond. VAAF:</span>
                      <span class="float-right">{{ analiseMatriculas.vaaf.toFixed(2) }}</span>
                    </div>
                    <div class="text-body-2">
                      <span class="font-weight-medium">Matrículas Pond. VAAT:</span>
                      <span class="float-right">{{ analiseMatriculas.vaat.toFixed(2) }}</span>
                    </div>
                  </v-col>
                </v-row>
              </v-sheet>
            </v-card-text>
          </v-card>
        </div>
      </v-slide-y-transition>
    </v-container>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      submittingForm: false,
      loadingParams: false,
      parametros: null,
      historico: [],
      municipio: {
        nome: "",
        uf: "",
        receitas_educacao: null,
        matriculas_por_segmento: {},
        condicionalidades: [false, false, false, false, false],
      },
      fatoresPonderacao: {},
      condicionalidades: [],
      resultado: null,
      resumo: [],
      oportunidades: [],
      modalidadesEspeciais: [
        "Educação especial (fatores 1,40-1,40)",
        "Educação indígena e quilombola (fatores 1,40-1,40)",
        "Educação do campo (+15% em todos os fatores)",
        "Formação técnica e profissional (fator 1,35)",
      ],
      analiseMatriculas: { total: 0, vaaf: 0, vaat: 0 },
    };
  },

  computed: {
    anoAtual() {
      const user = this.$store.getters["Auth/user"];
      return user && user.ano ? user.ano.descricao : new Date().getFullYear();
    },
  },

  async mounted() {
    await this.carregarParametros();
    await this.carregarHistorico();
  },

  methods: {
    async carregarParametros() {
      this.loadingParams = true;
      try {
        this.parametros = await this.$services.fundebService.getParameters(this.anoAtual);
        if (this.parametros) {
          this.fatoresPonderacao = this.parametros.fatores || {};
          this.condicionalidades = this.parametros.condicionalidades || [];
        }
      } catch (error) {
        // console.error("Erro ao carregar parâmetros do FUNDEB:", error);
      } finally {
        this.loadingParams = false;
      }
    },

    async carregarHistorico() {
      try {
        this.historico = await this.$services.fundebService.getHistory();
      } catch (error) {
        // console.error("Erro ao carregar histórico:", error);
      }
    },

    async calcularOrcamento() {
      if (!this.parametros) return;

      this.submittingForm = true;
      const municipio = this.municipio;

      const matriculasPonderadasVAAF = this.calcularMatriculasPonderadas(municipio, "vaaf");
      const matriculasPonderadasVAAT = this.calcularMatriculasPonderadas(municipio, "vaat");

      const complementoVAAF = this.calcularComplemento(municipio, "vaaf", matriculasPonderadasVAAF);
      const complementoVAAT = this.calcularComplemento(municipio, "vaat", matriculasPonderadasVAAT);
      const complementoVAAR = this.calcularComplementoVAAR(municipio, matriculasPonderadasVAAT);

      const incrementoTotal = complementoVAAF.valor + complementoVAAT.valor + complementoVAAR.valor;
      const orcamentoTotal = (municipio.receitas_educacao || 0) + incrementoTotal;
      const incrementoPercentual =
        municipio.receitas_educacao > 0 ? (incrementoTotal / municipio.receitas_educacao) * 100 : 0;

      this.resultado = {
        ano: this.anoAtual,
        municipio: municipio.nome,
        uf: municipio.uf,
        orcamentoBase: municipio.receitas_educacao,
        incrementoTotal,
        orcamentoTotal,
        incrementoPercentual,
        complementos: {
          vaaf: {
            valor: complementoVAAF.valor,
            recebe: complementoVAAF.recebe,
            label: `VAAF - Valor Anual por Aluno Fixo (${Number(this.parametros.p_vaaf).toFixed(
              1
            )}%)`,
            mensagem: `Matrículas Ponderadas: ${matriculasPonderadasVAAF.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
          },
          vaat: {
            valor: complementoVAAT.valor,
            recebe: complementoVAAT.recebe,
            label: `VAAT - Valor Anual Total por Aluno (${Number(this.parametros.p_vaat).toFixed(
              1
            )}%)`,
            mensagem: `Matrículas Ponderadas: ${matriculasPonderadasVAAT.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
          },
          vaar: {
            valor: complementoVAAR.valor,
            recebe: complementoVAAR.recebe,
            label: `VAAR - Valor Aluno-Ano por Resultado (${Number(this.parametros.p_vaar).toFixed(
              1
            )}%)`,
            mensagem: "Depende do cumprimento das 5 condicionalidades",
          },
        },
      };

      try {
        await this.$services.fundebService.storeCalculation({
          ano_id: this.parametros.ano_id,
          municipio: municipio.nome,
          uf: municipio.uf,
          receitas_educacao: municipio.receitas_educacao,
          matriculas_por_segmento: municipio.matriculas_por_segmento,
          condicionalidades: municipio.condicionalidades,
          resultado: this.resultado,
        });
        await this.carregarHistorico();
      } catch (error) {
        // console.error("Erro ao salvar cálculo:", error);
      } finally {
        this.submittingForm = false;
        this.preencherResumo();
        this.preencherOportunidades();
        this.preencherAnaliseMatriculas();
        this.$nextTick(() => {
          const el = document.getElementById("secao-resultado");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        });
      }
    },

    carregarCalculoHistorico(calc) {
      this.municipio.nome = calc.municipio;
      this.municipio.uf = calc.uf;
      this.municipio.receitas_educacao = calc.receitas_educacao;
      this.municipio.matriculas_por_segmento = calc.matriculas_por_segmento;
      this.municipio.condicionalidades = calc.condicionalidades;
      this.resultado = calc.resultado;
      this.preencherResumo();
      this.preencherOportunidades();
      this.preencherAnaliseMatriculas();
    },

    calcularMatriculasPonderadas(municipio, modalidade) {
      let total = 0;
      Object.entries(municipio.matriculas_por_segmento).forEach(([key, quantidade]) => {
        if (this.fatoresPonderacao[key]) {
          const fator = this.fatoresPonderacao[key];
          const fp = modalidade === "vaaf" ? fator.fp_vaaf : fator.fp_vaat;
          total += (quantidade || 0) * fp;
        }
      });
      return total;
    },

    calcularComplemento(municipio, modalidade, matriculasPonderadas) {
      if (!this.parametros || matriculasPonderadas === 0) return { valor: 0, recebe: false };
      const valorPorAluno = municipio.receitas_educacao / matriculasPonderadas;
      const valorMinimo =
        modalidade === "vaaf" ? this.parametros.vaaf_min : this.parametros.vaat_min;
      const precisaComplemento = valorPorAluno < valorMinimo;
      if (precisaComplemento) {
        const complemento = (valorMinimo - valorPorAluno) * matriculasPonderadas;
        return { valor: complemento, recebe: true };
      }
      return { valor: 0, recebe: false };
    },

    calcularComplementoVAAR(municipio, matriculasPonderadas) {
      if (!this.parametros) return { valor: 0, recebe: false };
      const condicionalidadesCumpridas = municipio.condicionalidades.filter((c) => c).length === 5;
      if (condicionalidadesCumpridas) {
        const valorVaarPorAluno = this.parametros.vaat_min * (this.parametros.p_vaar / 100);
        const complementoVaar = valorVaarPorAluno * matriculasPonderadas;
        return { valor: complementoVaar, recebe: true };
      }
      return { valor: 0, recebe: false };
    },

    preencherResumo() {
      this.resumo = [
        {
          titulo: "Orçamento Base",
          valor: this.formatarMoeda(this.resultado.orcamentoBase),
          color: "blue-lighten-5",
          borderColor: "#2196F3",
          valueColor: "#000",
        },
        {
          titulo: "Incremento Total",
          valor: this.formatarMoeda(this.resultado.incrementoTotal),
          color: "green-lighten-5",
          borderColor: "#4caf50",
          valueColor: "#388e3c",
        },
        {
          titulo: "Orçamento Total",
          valor: this.formatarMoeda(this.resultado.orcamentoTotal),
          color: "yellow-lighten-5",
          borderColor: "#fbc02d",
          valueColor: "#f57f17",
        },
        {
          titulo: "Incremento %",
          valor: isNaN(this.resultado.incrementoPercentual)
            ? "0.00%"
            : `${this.resultado.incrementoPercentual.toFixed(2)}%`,
          color: "purple-lighten-5",
          borderColor: "#9c27b0",
          valueColor: "#6a1b9a",
        },
      ];
    },

    preencherOportunidades() {
      const condFaltando = this.municipio.condicionalidades.filter((cond) => !cond).length;
      this.oportunidades = [
        {
          tipo: `✅ Elegível para VAAF (${Number(this.parametros.p_vaaf).toFixed(1)}%)`,
          valor: this.formatarMoeda(this.resultado.complementos.vaaf.valor),
          elegivel: this.resultado.complementos.vaaf.recebe,
          observacao: "",
        },
        {
          tipo: `✅ Elegível para VAAT (${Number(this.parametros.p_vaat).toFixed(1)}%)`,
          valor: this.formatarMoeda(this.resultado.complementos.vaat.valor),
          elegivel: this.resultado.complementos.vaat.recebe,
          observacao: "",
        },
        {
          tipo: condFaltando
            ? "❌ Não elegível para VAAR"
            : `✅ Elegível para VAAR (${Number(this.parametros.p_vaar).toFixed(1)}%)`,
          valor: this.formatarMoeda(this.resultado.complementos.vaar.valor),
          elegivel: this.resultado.complementos.vaar.recebe,
          observacao: this.resultado.complementos.vaar.recebe
            ? ""
            : `Necessário cumprir ${condFaltando} condicionalidade(s) adicional(is)`,
        },
      ];
    },

    preencherAnaliseMatriculas() {
      const totalMat = Object.values(this.municipio.matriculas_por_segmento).reduce(
        (sum, val) => sum + (val || 0),
        0
      );
      const ordenadas = Object.entries(this.municipio.matriculas_por_segmento)
        .filter(([_, q]) => (q || 0) > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);
      const segmentos = {};
      ordenadas.forEach(([seg, q]) => {
        segmentos[seg] = q;
      });
      this.analiseMatriculas = {
        total: totalMat,
        vaaf: this.calcularMatriculasPonderadas(this.municipio, "vaaf"),
        vaat: this.calcularMatriculasPonderadas(this.municipio, "vaat"),
        segmentos,
      };
    },

    calcularPercentual(quantidade) {
      const total = this.analiseMatriculas.total;
      return total > 0 ? ((quantidade / total) * 100).toFixed(1) : 0;
    },

    formatarMoeda(valor) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
        valor || 0
      );
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.border {
  border: 1px solid #e0e0e0;
}
.clickable-item {
  cursor: pointer;
  transition: background-color 0.2s;
}
.clickable-item:hover {
  background-color: #f5f5f5;
}
</style>
